import type { Article } from '@/lib/strapi-fetcher';

export interface RecommFeedResult {
  recommId: string | null;
  articles: Article[];
}

/**
 * Fetch a personalised "guess you like" feed. When the user is logged in, the
 * backend forwards a real user id; otherwise it degrades to popular+latest.
 *
 * The Next.js API route at `/api/news/recomm` enriches the raw item_ids with
 * full Strapi Article data so callers get the same Article type everywhere.
 */
export async function getRecommFeed(
  count = 10,
): Promise<RecommFeedResult> {
  const resp = await fetch('/api/news/recomm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'feed', count }),
    cache: 'no-store',
  });
  return resp.json();
}

export async function getRecommFeedNext(
  recommId: string,
  count = 10,
): Promise<RecommFeedResult> {
  const resp = await fetch('/api/news/recomm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'next', recommId, count }),
    cache: 'no-store',
  });
  return resp.json();
}

export async function getRecommRelated(
  itemId: string,
  count = 6,
): Promise<RecommFeedResult> {
  const resp = await fetch('/api/news/recomm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'related', itemId, count }),
    cache: 'no-store',
  });
  return resp.json();
}

export interface RecommEventPayload {
  item_id?: string;
  event_type: string;
  recomm_id?: string;
  properties?: Record<string, unknown>;
}

/**
 * Batch-upload news behavior events. Calls the Java backend directly;
 * user_id is injected server-side from the JWT.
 */
export async function uploadNewsEvents(
  events: RecommEventPayload[],
): Promise<void> {
  if (!events.length) return;
  const resp = await fetch('/api/news/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }),
    keepalive: true,
  });

  if (!resp.ok) {
    throw new Error(`news events upload failed: ${resp.status}`);
  }
}
